/* SKU search patch 2.5: prioritize products, reuse recent positive counts, remove artificial scroll delay. */
(function (root) {
  'use strict';
  if (root.document && root.AutoclinicVariantSku) return;
  const clean = value => typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  const unique = values => [...new Set(values.map(clean).filter(Boolean))];
  function requestedSkus(url) { return unique(url.searchParams.getAll('sku')); }
  function collectionContext(input) {
    const url = new URL(input);
    const match = url.pathname.match(/^(.*?)\/collections\/([^/]+)\/?$/);
    if (!match) throw new Error('Open this view from a collection URL.');
    return { url, root: match[1], handle: decodeURIComponent(match[2]) };
  }
  function skuQuery(skus) {
    return unique(skus).map(sku => 'variants.sku:"' + sku.replace(/([\\":()])/g, '\\$1') + '"').join(' OR ');
  }
  function dataUrl(input, page, skus = requestedSkus(new URL(input))) {
    const context = collectionContext(input);
    const url = new URL(context.root + '/search', context.url.origin);
    // Preserve native product/variant filters and theme preview, but never the old SKU metafields.
    for (const [key, value] of context.url.searchParams) {
      if ((key.startsWith('filter.') && !['filter.p.m.json.handle', 'filter.p.m.custom.skus'].includes(key)) || key === 'preview_theme_id') {
        url.searchParams.append(key, value);
      }
    }
    url.searchParams.set('view', 'autoclinic-sku-data');
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    url.searchParams.set('options[unavailable_products]', 'show');
    url.searchParams.set('q', skuQuery(skus));
    url.searchParams.set('page', String(page));
    return url;
  }
  function skuBatches(input, skus) {
    const batches = [];
    let current = [];
    for (const sku of unique(skus)) {
      const next = [...current, sku];
      if (current.length && (next.length > 30 || dataUrl(input, 1, next).href.length > 6000)) {
        batches.push(current);
        current = [sku];
      } else current = next;
      if (dataUrl(input, 1, current).href.length > 6000) throw new Error('A SKU or filter value is too long to search.');
    }
    if (current.length) batches.push(current);
    return batches;
  }
  function matchProduct(product, wanted) {
    if (!Array.isArray(product.variants) || product.variantCount !== product.variants.length) {
      throw new Error('A matching product has more than 250 variants. Expanded variant loading is required.');
    }
    const matches = product.variants.filter(v => wanted.has(clean(v.sku)) && v.matched === true);
    const variant = matches.find(v => v.available) || matches[0];
    return variant ? { ...product, variant } : null;
  }
  function preferredBrands(handle) {
    if (/^coilovers?(?:-|$)/i.test(handle)) return ['kw', 'st'];
    if (/^(?:tuning|ecu|throttle-controller)(?:s|-|$)/i.test(handle)) return ['dte', 'io'];
    return [];
  }
  function brandSortKey(value) {
    const brand = clean(value).toLowerCase().replace(/[^a-z0-9]/g, '');
    if (['kw', 'kwsuspension', 'kwsuspensions'].includes(brand)) return 'kw';
    if (['st', 'stsuspension', 'stsuspensions'].includes(brand)) return 'st';
    if (['dte', 'dtesystem', 'dtesystems'].includes(brand)) return 'dte';
    if (['io', 'iotuning'].includes(brand)) return 'io';
    return brand;
  }
  function compareBrandPrice(a, b, brands) {
    const aBrand = brandSortKey(a.vendor);
    const bBrand = brandSortKey(b.vendor);
    const rank = brand => brands.includes(brand) ? brands.indexOf(brand) : brands.length;
    return rank(aBrand) - rank(bBrand) || aBrand.localeCompare(bBrand) ||
      b.variant.price - a.variant.price || a.title.localeCompare(b.title);
  }

  function isThrottleController(handle) {
    return /^throttle-controllers?(?:-|$)/i.test(handle);
  }

  function throttleControllerRank(product) {
    const vendor = brandSortKey(product.vendor);
    const title = clean(product.title).toLowerCase();

    if (vendor === 'io' && /\bio[\s_-]*pedal\b/.test(title)) return 0;
    if (vendor === 'dte' && /\bpedal[\s_-]*box\b/.test(title)) {
      // Check the specific models first: both also contain "PedalBox".
      if (/\bpedal[\s_-]*box[\s_-]*pro\b/.test(title)) return 3;
      if (/\bpedal[\s_-]*box[\s_-]*(?:\+|plus\b)/.test(title)) return 2;
      return 1;
    }

    // Keep unrecognized products after the four requested product families.
    return 4;
  }

  function compareThrottleControllers(a, b) {
    return throttleControllerRank(a) - throttleControllerRank(b) ||
      compareBrandPrice(a, b, ['dte', 'io']);
  }

  async function collect(input, fetchPage, signal, progress = () => {}) {
    const context = collectionContext(input);
    const skus = requestedSkus(context.url);
    if (!skus.length) return [];
    const wanted = new Set(skus);
    const result = new Map();
    const batches = skuBatches(input, skus);
    let finished = 0;
    let nextBatch = 0;
    async function worker() {
      while (nextBatch < batches.length) {
        const batch = batches[nextBatch++];
        let pages = 1;
        let total = null;
        let seenCount = 0;
        const seenIds = new Set();
        for (let page = 1; page <= pages; page++) {
          if (signal?.aborted) throw new Error('Request cancelled');
          const data = await fetchPage(dataUrl(input, page, batch), signal);
          // query is informational: matching uses live variant.sku below, not the echoed text.
          if (!data || data.version !== 2 || data.source !== 'variant-sku-search') {
            throw new Error('SKU-HEADER: The response is not the expected search.autoclinic-sku-data.liquid version. Check the template in the theme being viewed.');
          }
          if (data.page !== page) {
            throw new Error(`SKU-PAGE: Expected page ${page}; received ${JSON.stringify(data.page)}.`);
          }
          if (!Array.isArray(data.products)) {
            throw new Error('SKU-PRODUCTS: The search response has no products array.');
          }
          if (!Number.isInteger(data.total) || !Number.isInteger(data.pages) || data.total < 0 || data.pages < 0) {
            throw new Error(`SKU-COUNTS: Invalid pagination values (total=${JSON.stringify(data.total)}, pages=${JSON.stringify(data.pages)}).`);
          }
          if (page === 1) {
            pages = Math.max(1, data.pages);
            total = data.total;
            if (total > 1000 || pages > 20) throw new Error('SKU search returned too many candidates. Refine the requested SKUs or filters.');
          } else if (data.total !== total || data.pages !== pages) {
            throw new Error('Search results changed while loading. Please retry.');
          }
          for (const product of data.products) {
            if (seenIds.has(product.id)) throw new Error('Search results changed while loading. Please retry.');
            seenIds.add(product.id);
            seenCount++;
            if (!Array.isArray(product.collectionHandles)) throw new Error('Invalid collection membership data.');
            if (context.handle !== 'all' && !product.collectionHandles.includes(context.handle)) continue;
            const match = matchProduct(product, wanted);
            if (match) result.set(product.id, match);
          }
        }
        if (seenCount !== total) throw new Error('Not all SKU search results were returned. Please retry.');
        finished++;
        progress(finished, batches.length);
      }
    }
    await Promise.all(Array.from({ length: Math.min(3, batches.length) }, worker));
    const items = [...result.values()];
    const sort = context.url.searchParams.get('sort_by');
    if (sort === 'price-ascending') items.sort((a, b) => a.variant.price - b.variant.price || a.title.localeCompare(b.title));
    else if (sort === 'price-descending') items.sort((a, b) => b.variant.price - a.variant.price || a.title.localeCompare(b.title));
    else if (sort === 'title-ascending') items.sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === 'title-descending') items.sort((a, b) => b.title.localeCompare(a.title));
    else if (sort === 'created-ascending') items.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
    else if (sort === 'created-descending') items.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    else if ((!sort || sort === 'manual') && isThrottleController(context.handle)) items.sort(compareThrottleControllers);
    else if (preferredBrands(context.handle).length) items.sort((a, b) => compareBrandPrice(a, b, preferredBrands(context.handle)));
    else items.sort((a, b) => skus.indexOf(clean(a.variant.sku)) - skus.indexOf(clean(b.variant.sku)) || a.title.localeCompare(b.title));
    return items;
  }
  function pageItems(items, page, size = 24) {
    const pages = Math.max(1, Math.ceil(items.length / size));
    const current = Math.min(pages, Math.max(1, Math.floor(Number(page)) || 1));
    return { page: current, pages, items: items.slice((current - 1) * size, current * size) };
  }
  function vendorOptions(products) {
    const counts = new Map();
    for (const product of products) {
      const vendor = clean(product.vendor);
      if (vendor) counts.set(vendor, (counts.get(vendor) || 0) + 1);
    }
    return [...counts].sort((a, b) => a[0].localeCompare(b[0]));
  }
  function filterVendor(products, vendor) {
    return vendor ? products.filter(product => clean(product.vendor) === vendor) : products;
  }
  const api = { vendorOptions, filterVendor, unique, requestedSkus, collectionContext, skuQuery, skuBatches, dataUrl, matchProduct, collect, pageItems };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document) return;
  root.AutoclinicVariantSku = api;
  const style = document.createElement('style');
  style.textContent = `
    a.jsc-no-product-available, a.jsc-no-product-available *{pointer-events:none!important;cursor:default!important;text-transform:none!important;opacity:1;}
    [data-ac-product-count][aria-busy="true"]{display:inline-block;width:1em;height:1em;margin-left:.3em;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;vertical-align:-.12em;animation:ac-count-spin .7s linear infinite}
    @keyframes ac-count-spin{to{transform:rotate(360deg)}}
    @media(prefers-reduced-motion:reduce){[data-ac-product-count][aria-busy="true"]{animation-duration:2s}}
    [data-autoclinic-sku-results][aria-busy="true"] [data-sku-status]{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:22px;box-sizing:border-box;min-height:300px;margin:24px 0 40px;padding:48px 24px;text-align:center;font-size:clamp(20px,2vw,26px);font-weight:700;line-height:1.4;color:#202020;background:linear-gradient(180deg,#fafafa,#fff);border-radius:12px}
    [data-autoclinic-sku-results][aria-busy="true"] [data-sku-status]::before{content:"";display:block;flex-shrink:0;width:48px;height:48px;box-sizing:border-box;border:4px solid #e8e8e8;border-top-color:#e00000;border-radius:50%;animation:ac-count-spin .8s linear infinite}
    @media(max-width:767px){[data-autoclinic-sku-results][aria-busy="true"] [data-sku-status]{min-height:240px;padding:32px 16px}}
    @media(prefers-reduced-motion:reduce){[data-autoclinic-sku-results][aria-busy="true"] [data-sku-status]::before{animation:none;border-top-color:#e00000}}
    [data-sku-pagination][aria-busy="true"] [data-ac-scroll-status]{display:flex;align-items:center;justify-content:center;gap:12px;min-height:48px}
    [data-sku-pagination][aria-busy="true"] [data-ac-scroll-status]::before{content:"";width:26px;height:26px;box-sizing:border-box;border:3px solid #e8e8e8;border-top-color:#e00000;border-radius:50%;animation:ac-count-spin .8s linear infinite}
    @media(prefers-reduced-motion:reduce){[data-sku-pagination][aria-busy="true"] [data-ac-scroll-status]::before{animation:none}}
    [data-autoclinic-sku-results] [data-sku-status].ac-sku-empty {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 14px;
      min-height: 260px;
      margin: 24px 0 40px;
      padding: 40px 24px;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      background: #fff;
      color: #111;
      font-family: inherit;
      text-align: center;
    }
    [data-autoclinic-sku-results] .ac-sku-empty p {max-width:680px;margin:0;}
    [data-autoclinic-sku-results] .ac-sku-empty__title {font-size:clamp(20px,2vw,26px);font-weight:700;line-height:1.35;}
    [data-autoclinic-sku-results] .ac-sku-empty__copy {font-size:16px;font-weight:400;line-height:1.7;color:#555;}
    [data-autoclinic-sku-results] .ac-sku-empty__select {color:#d60000;font-weight:700;text-decoration:underline;text-underline-offset:3px;cursor:pointer;}
    [data-autoclinic-sku-results] .ac-sku-empty__select:hover {color:#a90000;}
    [data-autoclinic-sku-results] .ac-sku-empty__select:focus-visible {outline:2px solid #d60000;outline-offset:4px;}
    @media(max-width:767px){[data-autoclinic-sku-results] [data-sku-status].ac-sku-empty{min-height:220px;padding:32px 16px;margin:16px 0 24px;}}
    [data-ac-native-sku] .ac-native-toolbar{flex-wrap:wrap}
    [data-ac-vendor-control]{display:flex;align-items:center;gap:14px}
    [data-ac-vendor-control] select{max-width:100%}
    @media(max-width:767px){[data-ac-vendor-control]{flex-wrap:wrap;max-width:100%}}
  `;
  document.head.append(style);

  // Count-only cache: never reuse product cards, prices, stock, or zero-result blocks.
  const countCacheKey = 'autoclinic-category-counts-v1';
  const countCacheTtl = 60000;
  let countCache = {};
  try {
    const saved = JSON.parse(sessionStorage.getItem(countCacheKey));
    if (saved && typeof saved === 'object' && !Array.isArray(saved)) countCache = saved;
  } catch (_) {}
  function recentCount(key) {
    const saved = countCache[key];
    const age = saved ? Date.now() - saved.time : Infinity;
    return age >= 0 && age < countCacheTtl && Number.isInteger(saved.count) && saved.count > 0
      ? saved.count : null;
  }
  function rememberCount(key, count) {
    if (!Number.isInteger(count) || count <= 0) { delete countCache[key]; return; }
    countCache[key] = { count, time: Date.now() };
    countCache = Object.fromEntries(Object.entries(countCache)
      .filter(([, value]) => value && Date.now() - value.time < countCacheTtl)
      .sort((a, b) => b[1].time - a[1].time).slice(0, 60));
    try { sessionStorage.setItem(countCacheKey, JSON.stringify(countCache)); } catch (_) {}
  }

  function startCategoryCounts() {
    const selector = 'a.card__link-list-item, a.jsc-link-list-car';
    let timer;
    let signature = null;
    let controller;
    let revision = 0;
    const badgeState = new WeakMap();
    const disabledLinks = new WeakMap();
    function disableLink(link) {
      if (!disabledLinks.has(link)) {
        disabledLinks.set(link, {
          href: link.getAttribute('href'),
          key: keyFor(link),
          tabindex: link.getAttribute('tabindex'),
          ariaDisabled: link.getAttribute('aria-disabled')
        });
      }
      const saved = disabledLinks.get(link);
      if (link.hasAttribute('href')) saved.href = link.getAttribute('href');
      if (link.hasAttribute('href')) link.removeAttribute('href');
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');
    }
    function restoreLink(link) {
      const saved = disabledLinks.get(link);
      if (!saved) return;
      for (const [name, value] of [
        ['href', link.getAttribute('href') ?? saved.href],
        ['tabindex', saved.tabindex],
        ['aria-disabled', saved.ariaDisabled]
      ]) {
        if (value === null) link.removeAttribute(name);
        else link.setAttribute(name, value);
      }
      disabledLinks.delete(link);
    }
    function blockUnavailable(event) {
      const link = event.target.closest?.(selector);
      if (!link || !disabledLinks.has(link)) return;
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    document.addEventListener('click', blockUnavailable, true);
    document.addEventListener('auxclick', blockUnavailable, true);
    document.addEventListener('keydown', blockUnavailable, true);
    function badge(link, value, state) {
      const token = state + ':' + value;

      if (badgeState.get(link) === token) {
        if (state === 'ready' && Number(value) === 0) disableLink(link);
        return;
      }

      badgeState.set(link, token);

      let span = link.querySelector(
        '[data-ac-product-count], .prod_count, .jsc-result-count'
      );

      if (!link.dataset.acOriginalLabel) {
        let originalLabel = '';

        for (const child of link.childNodes) {
          if (child.nodeType === 3) {
            originalLabel += child.textContent;
          }
        }

        originalLabel = originalLabel
          .replace(/\s*\(\d+(?: SKUs)?\)\s*$/, '')
          .trim();

        if (originalLabel) {
          link.dataset.acOriginalLabel = originalLabel;
        }
      }

      if (!span) {
        for (const child of link.childNodes) {
          if (child.nodeType === 3) {
            child.textContent = child.textContent.replace(
              /\s*\(\d+(?: SKUs)?\)\s*$/,
              ''
            );
          }
        }

        span = document.createElement('span');
        link.append(document.createTextNode(' '), span);
      }

      const originalLabel = link.dataset.acOriginalLabel || '';

      if (state === 'ready' && Number(value) === 0) {
        for (const child of link.childNodes) {
          if (child.nodeType === 3) {
            child.textContent = '';
          }
        }

        link.insertBefore(
          document.createTextNode('No product available'),
          link.firstChild
        );

        span.textContent = '';
        span.setAttribute('data-ac-product-count', '');
        span.setAttribute('aria-busy', 'false');
        span.setAttribute('aria-label', 'No product available');
        span.title = 'No product available';
        span.dataset.count = '0';

        link.classList.add('jsc-no-product-available');
        disableLink(link);

        return;
      }

      if (link.classList.contains('jsc-no-product-available')) {
        for (const child of link.childNodes) {
          if (
            child.nodeType === 3 &&
            child.textContent.trim() === 'No product available'
          ) {
            child.remove();
          }
        }

        if (originalLabel) {
          link.insertBefore(
            document.createTextNode(originalLabel),
            link.firstChild
          );
        }

        link.classList.remove('jsc-no-product-available');
        restoreLink(link);
      }

      span.setAttribute('data-ac-product-count', '');

      span.textContent =
        state === 'ready'
          ? `(${value})`
          : state === 'pending'
            ? ''
            : '(—)';

      span.setAttribute(
        'aria-busy',
        state === 'pending' ? 'true' : 'false'
      );

      span.setAttribute(
        'aria-label',
        state === 'pending'
          ? 'Checking product count'
          : state === 'ready'
            ? `${value} matching products`
            : 'Product count unavailable'
      );

      span.title =
        state === 'ready'
          ? `${value} matching products`
          : state === 'pending'
            ? 'Checking products'
            : 'Product count unavailable';

      if (state === 'ready') {
        span.dataset.count = String(value);
      } else {
        delete span.dataset.count;
      }
    }
    function keyFor(link) {
      const href = link.getAttribute('href') ?? disabledLinks.get(link)?.href;
      if (!href) return null;
      try {
        const url = new URL(href, location.origin);
        if (url.origin !== location.origin || !requestedSkus(url).length) return null;
        collectionContext(url);
        ['page', 'sku_page', 'sort_by', 'view', 'carId'].forEach(key => url.searchParams.delete(key));
        const skus = requestedSkus(url).sort();
        url.searchParams.delete('sku');
        skus.forEach(sku => url.searchParams.append('sku', sku));
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview && !url.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', preview);
        url.searchParams.sort();
        url.hash = '';
        return url.href;
      } catch { return null; }
    }
    async function countPage(request, signal) {
      const url = new URL(request);
      url.searchParams.set('view', 'autoclinic-sku-counts');
      const timeoutController = new AbortController();
      const abort = () => timeoutController.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) abort();
      const timeout = setTimeout(abort, 20000);
      try {
        const response = await fetch(url, { signal: timeoutController.signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Count request failed');
        return JSON.parse((await response.text()).replace(/^\uFEFF/, '').trim());
      } finally {
        clearTimeout(timeout);
        signal.removeEventListener('abort', abort);
      }
    }
    function refresh(force = false) {
      const links = [...document.querySelectorAll(selector)];
      const groups = new Map();
      for (const link of links) {
        const key = keyFor(link);
        const saved = disabledLinks.get(link);
        if (saved && saved.key === key && badgeState.get(link) === 'ready:0') disableLink(link);
        if (!key) {
          if (link.classList.contains('jsc-disabled-link')) badge(link, 0, 'ready');
          continue;
        }
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(link);
      }
      // The old selector calls this repeatedly. Do not repeat identical network work.
      const nextSignature = JSON.stringify([...groups].map(([key, members]) => [key, members.length]));
      if (!force && signature === nextSignature) return;
      signature = nextSignature;
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      const current = ++revision;
      const tasks = [...groups];
      for (const [, members] of tasks) members.forEach(link => badge(link, null, 'pending'));
      let next = 0;
      async function worker() {
        while (current === revision && next < tasks.length) {
          const [key, members] = tasks[next++];
          try {
            const cachedCount = force ? null : recentCount(key);
            const count = cachedCount === null ? (await collect(key, countPage, signal)).length : cachedCount;
            if (cachedCount === null) rememberCount(key, count);
            if (current !== revision || signal.aborted) return;
            members.forEach(link => {
              if (link.isConnected && keyFor(link) === key) badge(link, count, 'ready');
            });
          } catch (error) {
            if (current !== revision || signal.aborted) return;
            members.forEach(link => { if (link.isConnected && keyFor(link) === key) badge(link, null, 'error'); });
            console.warn('Autoclinic category product count unavailable:', error.message);
          }
        }
      }
      // One category at a time limits background traffic; SKU batching remains bounded.
      void worker();
    }
    // Coalesce changes without postponing work on every selector polling event.
    function schedule() {
      if (timer != null) return;
      timer = setTimeout(() => { timer = null; refresh(); }, 0);
    }
    window.addEventListener('autoclinic:category-links-updated', schedule);
    window.addEventListener('pageshow', event => { if (event.persisted) refresh(true); });
    const observer = new MutationObserver(changes => {
      if (changes.some(change => change.type === 'attributes' && change.target.matches(selector))) schedule();
      else if (changes.some(change => [...change.addedNodes].some(node => node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))))) {
        signature = null;
        schedule();
      }
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['href'] });
    schedule();
  }

  function start() {
    const host = document.querySelector('[data-autoclinic-sku-results]');
    if (!host || host.dataset.initialized) return;
    if (host.hasAttribute('data-ac-native-sku') && !new URLSearchParams(location.search).has('sku')) return;
    host.dataset.initialized = 'true';
    const status = host.querySelector('[data-sku-status]');
    const grid = host.querySelector('[data-sku-grid]');
    const nav = host.querySelector('[data-sku-pagination]');
    const form = host.querySelector('[data-sku-form]');
    const retry = host.querySelector('[data-sku-retry]');
    // Search does not expose collection best-selling/manual ranks.
    const sortSelect = form.querySelector('[name="sort_by"]');
    if (sortSelect) {
      const previous = sortSelect.value;
      for (const option of [...sortSelect.options]) {
        if (option.value === 'best-selling') option.remove();
        else if (option.value === 'manual') {
          const handle = collectionContext(location.href).handle;
          option.textContent = isThrottleController(handle)
            ? 'Recommended product order'
            : preferredBrands(handle).length ? 'Brand, then price: high to low' : 'SKU order';
        }
      }
      if (previous === 'best-selling') sortSelect.value = 'manual';
    }
    const skuInputs = host.querySelector('[data-sku-inputs]');
    const vendorControl = node('div', null);
    vendorControl.setAttribute('data-ac-vendor-control', '');
    const vendorSelect = node('select');
    vendorSelect.name = 'sku_vendor';
    vendorSelect.id = (sortSelect?.id || 'ac-sku-sort') + '-vendor';
    const vendorLabel = node('label', 'Brand', 'label');
    vendorLabel.htmlFor = vendorSelect.id;
    vendorSelect.append(new Option('Loading brands…', ''));
    vendorSelect.disabled = true;
    vendorControl.append(vendorLabel, vendorSelect);
    const toolbar = form.querySelector('.ac-native-toolbar');
    if (toolbar) toolbar.insertBefore(vendorControl, toolbar.querySelector('.col_breadcrumbs'));
    else form.append(vendorControl);
    function renderVendors(selected) {
      vendorSelect.replaceChildren(new Option('All brands', ''));
      const options = vendorOptions(results);
      options.forEach(([vendor, count]) => vendorSelect.append(new Option(`${vendor} (${count})`, vendor)));
      // Preserve a shared URL for a vendor with no current matches; never silently show all products.
      if (selected && !options.some(([vendor]) => vendor === selected)) vendorSelect.append(new Option(`${selected} (0)`, selected));
      vendorSelect.value = selected;
      vendorSelect.disabled = false;
    }
    vendorSelect.addEventListener('change', () => {
      const next = new URL(location.href);
      if (vendorSelect.value) next.searchParams.set('sku_vendor', vendorSelect.value);
      else next.searchParams.delete('sku_vendor');
      next.searchParams.delete('sku_page');
      previousUrl = next.href;
      history.pushState(null, '', next);
      render();
    });
    let controller;
    let runId = 0;
    let results = [];
    let loadedKey = '';
    let previousUrl = '';
    function node(tag, text, className) {
      const element = document.createElement(tag);
      if (text != null) element.textContent = text;
      if (className) element.className = className;
      return element;
    }
    function productCard(product) {
      const v = product.variant;
      if (!product.cardHtml || !v.priceHtml) throw new Error('SKU-CARD: Update the search data template and native card snippets.');
      const li = node('li', null, 'js-pagination-result jsc-product-grid');
      li.dataset.handle = product.handle || '';
      li.dataset.tags = (product.tags || []).join(',');
      const template = document.createElement('template');
      template.innerHTML = product.cardHtml;
      template.content.querySelectorAll('script').forEach(script => script.remove());
      const url = new URL(product.url, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid product URL');
      url.searchParams.set('variant', v.id);
      const carId = new URL(location.href).searchParams.get('carId');
      if (carId) url.searchParams.set('carId', carId);
      template.content.querySelectorAll('.js-prod-link, .card-link, .quick-add-view-btn').forEach(link => {
        link.setAttribute('href', url.pathname + url.search);
      });
      template.content.querySelectorAll('input[name="id"]').forEach(input => { input.value = v.id; });
      template.content.querySelectorAll('[data-product-url]').forEach(el => { el.dataset.productUrl = url.pathname + url.search; });
      template.content.querySelectorAll('.sku').forEach(el => { el.textContent = 'SKU: ' + v.sku; });
      template.content.querySelectorAll('[data-sku-price]').forEach(el => { el.innerHTML = v.priceHtml; });
      if (v.image) {
        const mainImage = template.content.querySelector('.card__media img');
        if (mainImage) {
          const imageUrl = new URL(v.image, location.origin);
          if (imageUrl.protocol === 'https:') {
            mainImage.src = imageUrl.href;
            mainImage.removeAttribute('srcset');
            const picture = mainImage.closest('picture');
            picture?.querySelectorAll('source').forEach(source => source.remove());
          }
        }
      }
      li.append(template.content);
      return li;
    }
    let visibleProducts = [];
    let renderedCount = 0;
    let scrollFrame = null;
    let batchTimer = null;
    const batchDelay = 0; // Products are already downloaded; do not add an artificial wait.
    const batchSize = Math.max(1, Math.floor(Number(host.dataset.pageSize) || 24));
    const moreButton = node('button', 'Load more products');
    moreButton.type = 'button';
    const scrollStatus = node('span');
    scrollStatus.setAttribute('data-ac-scroll-status', '');
    scrollStatus.setAttribute('role', 'status');
    scrollStatus.setAttribute('aria-live', 'polite');
    const sentinel = node('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height:1px;width:100%;';
    nav.after(sentinel);
    const scrollObserver = typeof root.IntersectionObserver === 'function'
      ? new root.IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) appendProducts();
        }, { rootMargin: '500px 0px' })
      : null;
    function stopScrollLoading() {
      clearTimeout(batchTimer);
      batchTimer = null;
      nav.setAttribute('aria-busy', 'false');
      moreButton.disabled = false;
      scrollObserver?.disconnect();
      if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
      scrollFrame = null;
      visibleProducts = [];
      renderedCount = 0;
    }
    function checkScrollPosition() {
      if (batchTimer !== null || scrollFrame !== null || renderedCount >= visibleProducts.length) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = null;
        if (!host.isConnected || host.getAttribute('aria-busy') === 'true') return;
        const bounds = sentinel.getBoundingClientRect();
        if (bounds.top <= window.innerHeight + 500 && bounds.bottom >= 0) appendProducts();
      });
    }
    function appendProducts(immediate = false) {
      if (batchTimer !== null || !host.isConnected || renderedCount >= visibleProducts.length) return;
      if (immediate) { commitProducts(); return; }
      nav.setAttribute('aria-busy', 'true');
      scrollStatus.textContent = 'Loading more products…';
      moreButton.disabled = true;
      batchTimer = setTimeout(() => {
        batchTimer = null;
        nav.setAttribute('aria-busy', 'false');
        moreButton.disabled = false;
        if (!host.isConnected) return;
        try { commitProducts(); }
        catch (error) {
          scrollObserver?.disconnect();
          scrollStatus.textContent = 'Unable to show more products. Please try again.';
          console.warn('Autoclinic product cards:', error);
        }
      }, batchDelay);
    }
    function commitProducts() {
      // Build the entire batch before appending so a card error cannot leave duplicates.
      const cards = visibleProducts.slice(renderedCount, renderedCount + batchSize).map(productCard);
      grid.append(...cards);
      renderedCount += cards.length;
      const hasMore = renderedCount < visibleProducts.length;
      moreButton.hidden = !hasMore;
      scrollStatus.textContent = `Showing ${renderedCount} of ${visibleProducts.length} products`;
      if (!hasMore) scrollObserver?.disconnect();
      // Fill short viewports even when the sentinel never leaves the observer margin.
      checkScrollPosition();
    }
    moreButton.addEventListener('click', () => appendProducts());
    window.addEventListener('scroll', checkScrollPosition, { passive: true });
    window.addEventListener('resize', checkScrollPosition);
    function renderUnavailableMessage() {
      status.classList.add('ac-sku-empty');
      status.innerHTML = `
        <p class="ac-sku-empty__title">The parts are unavailable for your selected vehicle.</p>
        <p class="ac-sku-empty__copy">Please <span class="cursor d-inline-block underline fontWeight-9 ac-sku-empty__select" role="button" tabindex="0" onclick="changeVehicleTrigger(this,'page')">select</span> a different vehicle to proceed. If you need assistance, please contact us.</p>
      `;
      // Give the inline selector action the same keyboard support as a button.
      status.querySelector('.ac-sku-empty__select').addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.currentTarget.click();
        }
      });
    }
    function render() {
      stopScrollLoading();
      const url = new URL(location.href);
      const selectedVendor = url.searchParams.get('sku_vendor') || '';
      renderVendors(selectedVendor);
      visibleProducts = filterVendor(results, selectedVendor);
      grid.replaceChildren();
      nav.replaceChildren(scrollStatus, moreButton);
      nav.style.flexWrap = 'wrap';
      moreButton.hidden = true;
      scrollStatus.textContent = '';
      status.classList.remove('ac-sku-empty');
      if (visibleProducts.length) {
        status.textContent = `${visibleProducts.length} matching product${visibleProducts.length === 1 ? '' : 's'}`;
      } else {
        renderUnavailableMessage();
      }
      host.setAttribute('aria-busy', 'false');
      // Old pagination links still include all earlier products, with none skipped.
      const legacyPage = pageItems(visibleProducts, url.searchParams.get('sku_page'), batchSize).page;
      for (let page = 0; page < legacyPage; page++) appendProducts(true);
      if (renderedCount < visibleProducts.length) scrollObserver?.observe(sentinel);
    }
    async function load(force = false) {
      const url = new URL(location.href);
      const keyUrl = new URL(url);
      keyUrl.searchParams.delete('sku_page');
      keyUrl.searchParams.delete('sku_vendor');
      const key = keyUrl.href;
      previousUrl = url.href;
      if (!force && key === loadedKey) { render(); return; }
      stopScrollLoading();
      controller?.abort();
      controller = new AbortController();
      const id = ++runId;
      loadedKey = '';
      results = [];
      vendorSelect.disabled = true;
      vendorSelect.replaceChildren(new Option('Loading brands…', ''));
      grid.replaceChildren();
      nav.replaceChildren();
      retry.hidden = true;
      status.classList.remove('ac-sku-empty');
      const skus = requestedSkus(url);
      skuInputs.replaceChildren();
      // Keep preview context when using the standalone full-page filter form.
      for (const name of ['sku', 'carId', 'preview_theme_id']) {
        for (const value of url.searchParams.getAll(name)) {
          const input = node('input');
          input.type = 'hidden'; input.name = name; input.value = value;
          skuInputs.append(input);
        }
      }
      if (!skus.length) {
        status.textContent = 'Choose your vehicle and a product category to see compatible products.';
        host.setAttribute('aria-busy', 'false');
        return;
      }
      host.setAttribute('aria-busy', 'true');
      status.textContent = 'Loading products…';
      try {
        const nextResults = await collect(url, async (request, signal) => {
          const requestController = new AbortController();
          const abort = () => requestController.abort();
          signal.addEventListener('abort', abort, { once: true });
          if (signal.aborted) abort();
          const timer = setTimeout(abort, 20000);
          try {
            const response = await fetch(request, { signal: requestController.signal, cache: 'no-store', credentials: 'same-origin' });
            if (!response.ok) throw new Error('Unable to load products. Please retry.');
            const body = await response.text();
            try { return JSON.parse(body.replace(/^\uFEFF/, '').trim()); }
            catch {
              if (/Liquid (?:error|syntax error)/i.test(body)) {
                throw new Error('SKU-LIQUID: Shopify reported a Liquid error in the search data template.');
              }
              if (/<!doctype html|<html[\s>]/i.test(body)) {
                throw new Error('SKU-HTML: Shopify returned a webpage instead of search data. Check that the search template is saved in the theme being viewed.');
              }
              throw new Error('SKU-JSON: The search template returned malformed JSON.');
            }
          } catch (error) {
            if (requestController.signal.aborted && !signal.aborted) throw new Error('Product search took too long. Please retry.');
            throw error;
          } finally {
            clearTimeout(timer);
            signal.removeEventListener('abort', abort);
          }
        }, controller.signal, (page, pages) => {
          if (id === runId) status.textContent = pages > 1 ? `Loading products… ${page} of ${pages} SKU groups` : 'Loading products…';
        });
        if (id !== runId) return;
        results = nextResults;
        loadedKey = key;
        render();
      } catch (error) {
        if (id !== runId) return;
        controller.abort();
        stopScrollLoading();
        nav.replaceChildren();
        loadedKey = '';
        grid.replaceChildren();
        status.textContent = error.message;
        retry.hidden = false;
        host.setAttribute('aria-busy', 'false');
      }
    }
    // This isolated form navigates normally, avoiding the existing theme's AJAX filter handler.
    form.addEventListener('submit', event => {
      event.preventDefault();
      event.stopPropagation();
      const next = new URL(form.action, location.origin);
      const params = new URLSearchParams(new FormData(form));
      for (const [name, value] of [...params]) { if (value === '') params.delete(name); }
      next.search = params.toString();
      location.assign(next.href);
    });
    form.querySelectorAll('[data-sku-auto-submit]').forEach(select => select.addEventListener('change', () => form.requestSubmit()));
    retry.addEventListener('click', () => load(true));
    window.addEventListener('popstate', () => load());
    window.addEventListener('pageshow', event => { if (event.persisted) load(true); });
    window.addEventListener('pushstate', () => { if (location.href !== previousUrl) load(); });
    window.addEventListener('replacestate', () => { if (location.href !== previousUrl) load(); });
    return load();
  }
  function initialize() {
    // Product requests take priority over counts on the initial collection load.
    Promise.resolve(start()).then(startCategoryCounts, error => {
      console.warn('Autoclinic SKU initialization:', error);
      startCategoryCounts();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})(typeof window === 'undefined' ? globalThis : window);
